import { computeClimateCapacityAdjustment, normalizeClimateDatabaseSummary } from "./climateConfig";

export function climateLevelMeta(level = "attention") {
  if (level === "favorable") return { label: "Favorável", color: "#22C55E", icon: "✅" };
  if (level === "critical") return { label: "Crítica", color: "#EF4444", icon: "🛑" };
  if (level === "restrictive") return { label: "Restritiva", color: "#F97316", icon: "⚠️" };
  return { label: "Atenção", color: "#F59E0B", icon: "👀" };
}

function centroidFromPolygon(polygon = []) {
  const pts = Array.isArray(polygon) ? polygon.map((pt) => ({ lat: Number(pt?.lat), lng: Number(pt?.lng) })).filter((pt) => Number.isFinite(pt.lat) && Number.isFinite(pt.lng)) : [];
  if (!pts.length) return { latitude: null, longitude: null };
  return {
    latitude: pts.reduce((sum, pt) => sum + pt.lat, 0) / pts.length,
    longitude: pts.reduce((sum, pt) => sum + pt.lng, 0) / pts.length,
  };
}

function resolveEntityCoordinates(entity) {
  const latitude = Number(entity?.lat ?? entity?.latitude);
  const longitude = Number(entity?.lng ?? entity?.longitude);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) return { latitude, longitude };
  return centroidFromPolygon(entity?.polygon || []);
}

export function resolveOperationalClimateContext(record = {}, pastures = [], pois = []) {
  const poiId = String(record?.poiId || "").trim();
  const location = String(record?.location || record?.pasture || record?.place || "").trim().toLowerCase();
  const poi = (Array.isArray(pois) ? pois : []).find((item) => String(item?.id || "").trim() === poiId)
    || (Array.isArray(pois) ? pois : []).find((item) => String(item?.name || item?.label || "").trim().toLowerCase() === location);
  if (poi) {
    const coords = resolveEntityCoordinates(poi);
    return {
      contextType: "poi",
      contextId: String(poi?.id || poiId || "").trim(),
      contextName: String(poi?.name || poi?.label || record?.location || "Ponto de interesse").trim(),
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
  }
  const pasture = (Array.isArray(pastures) ? pastures : []).find((item) => String(item?.id || "").trim() === String(record?.pastureId || "").trim())
    || (Array.isArray(pastures) ? pastures : []).find((item) => String(item?.name || item?.label || "").trim().toLowerCase() === location);
  if (pasture) {
    const coords = resolveEntityCoordinates(pasture);
    return {
      contextType: "pasture",
      contextId: String(pasture?.id || record?.pastureId || "").trim(),
      contextName: String(pasture?.name || pasture?.label || record?.location || "Pastagem").trim(),
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
  }
  return {
    contextType: "farm",
    contextId: "",
    contextName: String(record?.location || "Fazenda").trim() || "Fazenda",
    latitude: null,
    longitude: null,
  };
}

function alertLevelScore(level = "info") {
  if (level === "critical") return 3;
  if (level === "warning" || level === "attention") return 2;
  if (level === "info") return 1;
  return 0;
}

function computeLevel({ rainScore = 0, heatScore = 0, dryScore = 0, alertScore = 0 }) {
  const peak = Math.max(rainScore, heatScore, dryScore, alertScore, 0);
  const strongCount = [rainScore, heatScore, dryScore, alertScore].filter((value) => value >= 3).length;
  if (peak >= 3 && strongCount >= 2) return "critical";
  if (peak >= 3) return "restrictive";
  if (peak >= 2) return "attention";
  return "favorable";
}

export function buildOperationalClimatePanel({ summary, forecast, alerts, profile, contextName = "" } = {}) {
  const normalizedSummary = normalizeClimateDatabaseSummary(summary);
  const climateCalc = computeClimateCapacityAdjustment(profile, normalizedSummary);
  const today = forecast?.today || {};
  const nextDays = Array.isArray(forecast?.days) ? forecast.days.slice(0, 2) : [];
  const activeAlerts = Array.isArray(alerts?.active_alerts) ? alerts.active_alerts : [];
  const activeAlertScore = activeAlerts.length ? Math.max(...activeAlerts.map((item) => alertLevelScore(String(item?.level || "info")))) : 0;
  const todayRainChance = Number(today?.rain_chance_pct || 0);
  const next48RainChance = nextDays.length ? Math.max(...nextDays.map((item) => Number(item?.rain_chance_pct || 0))) : todayRainChance;
  const todayRainMm = Number(today?.rainfall_mm || 0);
  const next48RainMm = nextDays.length ? nextDays.reduce((sum, item) => sum + Number(item?.rainfall_mm || 0), 0) : todayRainMm;
  const next48MaxTemp = nextDays.length ? Math.max(...nextDays.map((item) => Number(item?.temp_max_c || 0))) : Number(today?.temp_max_c || 0);
  const dryDays = Number(climateCalc?.dryDays || 0);
  const rainScore = next48RainChance >= 75 || next48RainMm >= 25 ? 3 : ((next48RainChance >= 45 || next48RainMm >= 10) ? 2 : 1);
  const heatScore = next48MaxTemp >= 35 ? 3 : (next48MaxTemp >= 31 ? 2 : 1);
  const dryScore = dryDays >= 18 ? 3 : (dryDays >= 10 ? 2 : 1);
  const level = computeLevel({ rainScore, heatScore, dryScore, alertScore: activeAlertScore });
  const meta = climateLevelMeta(level);
  const signals = [
    { id: "rain", icon: "🌧️", label: "Chuva", value: next48RainChance ? `${next48RainChance.toFixed(0)}%` : (next48RainMm ? `${next48RainMm.toFixed(1)} mm` : "Baixo"), tone: rainScore >= 3 ? "restrictive" : (rainScore >= 2 ? "attention" : "favorable") },
    { id: "heat", icon: "🥵", label: "Calor", value: next48MaxTemp ? `${next48MaxTemp.toFixed(1)} °C` : "Sem pico", tone: heatScore >= 3 ? "restrictive" : (heatScore >= 2 ? "attention" : "favorable") },
    { id: "dry", icon: "☀️", label: "Seca", value: `${Math.round(dryDays)} dia(s)`, tone: dryScore >= 3 ? "restrictive" : (dryScore >= 2 ? "attention" : "favorable") },
  ];
  const headline = level === "critical" ? "Condição climática crítica para execução mais exposta." : (level === "restrictive" ? "A condição climática pede mais cautela antes de avançar a operação." : (level === "attention" ? "O clima não trava a operação, mas merece atenção no curto prazo." : "A condição climática está mais confortável para a rotina operacional."));
  const recommendation = rainScore >= 2 ? "Revise janela seca e risco de interrupção por chuva antes de deslocar equipe ou equipamento." : (heatScore >= 2 ? "Concentre a execução mais pesada em horários menos quentes e reforce água/sombra." : (dryScore >= 2 ? "Acompanhe suporte hídrico e resposta da área antes de ampliar manejo ou permanência." : "Aproveite a janela mais estável para organizar execução e acompanhamento do registro."));
  return {
    level,
    meta,
    headline,
    recommendation,
    contextLabel: contextName || "Fazenda",
    observedLabel: normalizedSummary?.latestObservedOn || "",
    sourceLabel: normalizedSummary?.sourceStationName || normalizedSummary?.sourceStationId || "Sem estação",
    summaryLabel: climateCalc?.summary || normalizedSummary?.summary || "Sem leitura climática no banco.",
    signals,
  };
}
